import React, { useRef, useState } from "react";
import { ChatDisclaimer } from "@/components/AiNotice";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import ReactMarkdown from "react-markdown";
import { ArrowLeft, Zap, Sun, Loader2, Send, Swords } from "lucide-react";
import client from "@/lib/api";
import { useLang } from "@/lib/lang";
import VoiceButton from "@/components/VoiceButton";

const SESSION_KEY = "lechatceltique.debate_session";

const EXAMPLES = {
  fr: [
    "Je veux quitter mon travail stable pour tenter une aventure artistique.",
    "Je dois décider si je pardonne à un ami qui m'a trahi.",
    "Faut-il couper les ponts avec un parent toxique ?",
    "Comment affronter une peur que j'évite depuis 3 ans ?",
  ],
  en: [
    "I want to quit my stable job to pursue art.",
    "Should I forgive a friend who betrayed me?",
    "Should I cut ties with a toxic parent?",
    "How do I face a fear I've avoided for 3 years?",
  ],
};

const THUNDER = `${process.env.REACT_APP_BACKEND_URL}/api/static/audio/zeus_thunder.mp3`;
const LYRE = `${process.env.REACT_APP_BACKEND_URL}/api/static/audio/apollon_lyre.mp3`;

export default function DebatOlympien() {
  const { lang, setLang } = useLang();
  const [question, setQuestion] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null); // { question, zeus_reply, apollon_reply, session_id }
  const [sessionId, setSessionId] = useState(() => localStorage.getItem(SESSION_KEY) || "");
  const thunderRef = useRef(null);
  const lyreRef = useRef(null);

  const ask = async (text) => {
    const q = (text ?? question).trim();
    if (!q) return;
    setLoading(true);
    setResult(null);
    try {
      const r = await client.post("/debate/olympien", { question: q, session_id: sessionId || null, lang });
      const data = r.data;
      if (data.session_id && data.session_id !== sessionId) {
        setSessionId(data.session_id);
        localStorage.setItem(SESSION_KEY, data.session_id);
      }
      setResult(data);
      // Play both signature sounds staggered — thunder first, lyre 400ms later
      try {
        if (thunderRef.current) { thunderRef.current.volume = 0.5; thunderRef.current.currentTime = 0; thunderRef.current.play().catch(()=>{}); }
        setTimeout(() => {
          if (lyreRef.current) { lyreRef.current.volume = 0.45; lyreRef.current.currentTime = 0; lyreRef.current.play().catch(()=>{}); }
        }, 400);
      } catch { /* ignore */ }
    } catch (err) {
      if (err?.response?.status === 429) {
        toast.error(lang === "fr"
          ? "Les dieux récupèrent leur souffle. Attends une minute avant une nouvelle question."
          : "The gods catch their breath. Wait a minute before a new question.");
      } else {
        toast.error(lang === "fr" ? "L'Olympe se tait. Réessaie dans un instant." : "Olympus falls silent. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="debate-page" className="min-h-screen relative"
         style={{
           background: "radial-gradient(ellipse at top, #1a1428 0%, #0e0a1a 55%, #04030b 100%)",
           color: "#f8f4ff",
           overflowX: "hidden",
         }}>
      <audio ref={thunderRef} src={THUNDER} preload="auto" />
      <audio ref={lyreRef} src={LYRE} preload="auto" />

      <header className="relative z-30">
        <div className="max-w-6xl mx-auto px-5 sm:px-8 py-6 flex items-center justify-between">
          <Link to="/" className="inline-flex items-center gap-2 text-sm font-mono uppercase tracking-[0.24em]" style={{ color: "#d4af37" }}>
            <ArrowLeft size={14} /> {lang === "fr" ? "Retour" : "Back"}
          </Link>
          <div className="flex items-center rounded-full text-[11px] font-mono overflow-hidden"
               style={{ background: "rgba(60,45,100,0.3)", border: "1px solid rgba(214,193,255,0.3)" }}>
            <button onClick={() => setLang("fr")} type="button" data-testid="debate-lang-fr" className="px-2.5 py-1.5"
                    style={{ background: lang === "fr" ? "#d4af37" : "transparent", color: lang === "fr" ? "#0a0612" : "#e4d7ff", fontWeight: lang === "fr" ? 600 : 400 }}>FR</button>
            <button onClick={() => setLang("en")} type="button" data-testid="debate-lang-en" className="px-2.5 py-1.5"
                    style={{ background: lang === "en" ? "#d4af37" : "transparent", color: lang === "en" ? "#0a0612" : "#e4d7ff", fontWeight: lang === "en" ? 600 : 400 }}>EN</button>
          </div>
        </div>
      </header>

      <section className="relative z-10 max-w-5xl mx-auto px-5 sm:px-8 pt-4 pb-10">
        <div className="overline mb-4" style={{ color: "#d4af37" }}>
          <span className="kicker-line" />
          {lang === "fr" ? "Débat olympien · Zeus ⚔️ Apollon · deux voix, une question" : "Olympian debate · Zeus ⚔️ Apollo · two voices, one question"}
        </div>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02]" style={{ color: "#f8f4ff" }}>
          {lang === "fr" ? (
            <><span className="italic" style={{ color: "#ffd84a" }}>Zeus</span> & <span className="italic" style={{ color: "#ff9a4a" }}>Apollon</span>,
              <br /><span style={{ color: "#c5b8d9" }}>deux dieux, une seule question.</span></>
          ) : (
            <><span className="italic" style={{ color: "#ffd84a" }}>Zeus</span> & <span className="italic" style={{ color: "#ff9a4a" }}>Apollo</span>,
              <br /><span style={{ color: "#c5b8d9" }}>two gods, one single question.</span></>
          )}
        </h1>
        <p className="mt-5 max-w-3xl text-base sm:text-lg" style={{ color: "#c5b8d9" }}>
          {lang === "fr"
            ? "Pose une question. Les deux dieux répondent EN PARALLÈLE. Pas de vérité unique — deux lumières légitimes qui t'aident à décider par toi-même. L'oracle meurt ici ; ton libre-arbitre naît."
            : "Ask one question. Both gods answer IN PARALLEL. No single truth — two legitimate lights helping you decide for yourself. Oracle dies here; your free will is born."}
        </p>

        <form
          onSubmit={(e) => { e.preventDefault(); ask(); }}
          className="mt-8 flex flex-col sm:flex-row gap-3 items-stretch sm:items-end"
        >
          <textarea
            data-testid="debate-input"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); ask(); } }}
            placeholder={lang === "fr" ? "Pose ta question aux deux dieux…" : "Ask both gods your question…"}
            rows={2}
            style={{
              flex: 1,
              resize: "none",
              padding: "14px 16px",
              borderRadius: 14,
              background: "rgba(0,0,0,0.4)",
              border: "1px solid rgba(214,175,55,0.3)",
              color: "#f8f4ff",
              fontSize: 15,
              outline: "none",
            }}
            disabled={loading}
          />
          <button
            type="submit"
            disabled={loading || !question.trim()}
            data-testid="debate-ask"
            className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-full"
            style={{
              background: "linear-gradient(135deg, #ffd84a, #ff9a4a)",
              color: "#0a0612",
              fontFamily: "'IBM Plex Mono', monospace",
              fontSize: 13,
              fontWeight: 700,
              letterSpacing: "0.1em",
              textTransform: "uppercase",
              opacity: (loading || !question.trim()) ? 0.4 : 1,
              boxShadow: "0 20px 50px -20px rgba(255,154,74,0.6)",
            }}
          >
            {loading ? <Loader2 size={16} className="animate-spin" /> : <Swords size={16} />}
            {loading
              ? (lang === "fr" ? "Les dieux parlent…" : "The gods speak…")
              : (lang === "fr" ? "⚔️ Convoquer les deux" : "⚔️ Summon both")}
          </button>
        </form>
        <ChatDisclaimer testId="debatolympien-ai-disclaimer" />

        <div className="mt-5 flex flex-wrap gap-2">
          <span className="text-[10px] font-mono uppercase tracking-[0.2em] self-center mr-2" style={{ color: "#8a7cad" }}>
            {lang === "fr" ? "Exemples :" : "Examples:"}
          </span>
          {EXAMPLES[lang].map((ex, i) => (
            <button
              key={i}
              type="button"
              onClick={() => { setQuestion(ex); ask(ex); }}
              disabled={loading}
              data-testid={`debate-example-${i}`}
              style={{
                padding: "6px 12px",
                borderRadius: 999,
                background: "rgba(214,175,55,0.12)",
                border: "1px solid rgba(214,175,55,0.35)",
                color: "#e4d7ff",
                fontSize: 11,
                fontFamily: "'IBM Plex Mono', monospace",
                opacity: loading ? 0.5 : 1,
                cursor: loading ? "not-allowed" : "pointer",
              }}
            >
              {ex}
            </button>
          ))}
        </div>
      </section>

      {/* Results */}
      {(loading || result) && (
        <section className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 pb-24">
          {result?.question && (
            <div
              data-testid="debate-question-echo"
              className="mb-6 p-4 rounded-xl text-center"
              style={{
                background: "rgba(214,175,55,0.1)",
                border: "1px solid rgba(214,175,55,0.3)",
                color: "#e4d7ff",
                fontStyle: "italic",
              }}
            >
              « {result.question} »
            </div>
          )}

          <div className="grid gap-6 lg:grid-cols-2">
            {/* Zeus column */}
            <article data-testid="debate-zeus-col"
                     className="p-6 rounded-2xl relative overflow-hidden"
                     style={{
                       background: "linear-gradient(180deg, rgba(42,26,4,0.95), rgba(14,8,2,0.98))",
                       border: "1.5px solid rgba(255,216,74,0.4)",
                       boxShadow: "0 24px 60px -30px rgba(255,216,74,0.5)",
                     }}>
              <div className="flex items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1px dashed rgba(255,216,74,0.3)" }}>
                <div className="w-11 h-11 rounded-full flex items-center justify-center"
                     style={{ background: "rgba(255,216,74,0.2)", border: "1px solid rgba(255,216,74,0.6)" }}>
                  <Zap size={18} style={{ color: "#ffd84a" }} />
                </div>
                <div>
                  <h3 className="font-serif text-xl" style={{ color: "#ffd84a" }}>Zeus</h3>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "#c89a4e" }}>
                    {lang === "fr" ? "Roi de l'Olympe · parole foudroyante" : "King of Olympus · thunderous word"}
                  </p>
                </div>
              </div>
              {loading && !result ? (
                <div className="flex items-center gap-2 py-6" style={{ color: "#f8dc8c" }}>
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">{lang === "fr" ? "La foudre se charge…" : "Thunder is gathering…"}</span>
                </div>
              ) : (
                <>
                  <div style={{ color: "#f8dc8c", fontSize: 14.5, lineHeight: 1.7 }}>
                    <ReactMarkdown>{result?.zeus_reply || ""}</ReactMarkdown>
                  </div>
                  {result?.zeus_reply && (
                    <div className="mt-4">
                      <VoiceButton text={result.zeus_reply} lang={lang} accent="#ffd84a" testId="debate-zeus-voice" />
                    </div>
                  )}
                </>
              )}
            </article>

            {/* Apollon column */}
            <article data-testid="debate-apollon-col"
                     className="p-6 rounded-2xl relative overflow-hidden"
                     style={{
                       background: "linear-gradient(180deg, rgba(42,24,8,0.95), rgba(14,6,2,0.98))",
                       border: "1.5px solid rgba(255,154,74,0.4)",
                       boxShadow: "0 24px 60px -30px rgba(255,154,74,0.5)",
                     }}>
              <div className="flex items-center gap-3 mb-4 pb-3" style={{ borderBottom: "1px dashed rgba(255,154,74,0.3)" }}>
                <div className="w-11 h-11 rounded-full flex items-center justify-center"
                     style={{ background: "rgba(255,154,74,0.2)", border: "1px solid rgba(255,154,74,0.6)" }}>
                  <Sun size={18} style={{ color: "#ff9a4a" }} />
                </div>
                <div>
                  <h3 className="font-serif text-xl" style={{ color: "#ff9a4a" }}>Apollon</h3>
                  <p className="text-[10px] font-mono uppercase tracking-[0.2em]" style={{ color: "#c8864e" }}>
                    {lang === "fr" ? "Ami lumineux · plusieurs options" : "Luminous friend · multiple options"}
                  </p>
                </div>
              </div>
              {loading && !result ? (
                <div className="flex items-center gap-2 py-6" style={{ color: "#f5c89a" }}>
                  <Loader2 size={16} className="animate-spin" />
                  <span className="text-sm">{lang === "fr" ? "La lyre s'accorde…" : "The lyre is tuning…"}</span>
                </div>
              ) : (
                <>
                  <div style={{ color: "#f5c89a", fontSize: 14.5, lineHeight: 1.7 }}>
                    <ReactMarkdown>{result?.apollon_reply || ""}</ReactMarkdown>
                  </div>
                  {result?.apollon_reply && (
                    <div className="mt-4">
                      <VoiceButton text={result.apollon_reply} lang={lang} accent="#ff9a4a" testId="debate-apollon-voice" />
                    </div>
                  )}
                </>
              )}
            </article>
          </div>

          {result && (
            <div
              data-testid="debate-arbiter"
              className="mt-8 p-5 rounded-2xl text-center"
              style={{
                background: "linear-gradient(180deg, rgba(60,45,100,0.5), rgba(20,15,40,0.7))",
                border: "1px solid rgba(214,175,55,0.4)",
              }}
            >
              <p className="text-sm" style={{ color: "#e4d7ff" }}>
                {lang === "fr"
                  ? "⚖️ Deux voix légitimes. Aucune n'est LA vérité. C'est à toi de peser, puis d'agir — ou de laisser mûrir. Le libre-arbitre est ton seul oracle."
                  : "⚖️ Two legitimate voices. Neither is THE truth. Yours to weigh, then act — or let it ripen. Free will is your only oracle."}
              </p>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
